import { Form, Input, Modal, message, Progress, Select, Upload } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { appAssetUploadApi } from '@/services/appAssetUploadApi';
import { applicationStore } from '@/stores';
import type {
  AppCatalogAppItem,
  AppCatalogStatus,
  CreateAdminAppCatalogAppRequest,
  UpdateAdminAppCatalogAppRequest,
} from '@/types';
import { APP_STATUS_OPTIONS } from '../constants';

const { TextArea } = Input;

interface AppFormDialogProps {
  open: boolean;
  app: AppCatalogAppItem | null;
  onOpenChange: (open: boolean) => void;
}

interface AppFormValues {
  appId: string;
  displayNameKey?: string;
  fallbackDisplayName: string;
  descriptionKey?: string;
  fallbackDescription?: string;
  iconUrl?: string;
  categories?: string[];
  status: AppCatalogStatus;
  reason?: string;
}

function normalizeCategories(values?: string[]) {
  return Array.from(
    new Set((values ?? []).map((value) => value.trim()).filter(Boolean)),
  );
}

function trimOptional(value?: string) {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function defaultDisplayNameKey(appId: string) {
  return `app.${appId}.name`;
}

export const AppFormDialog = observer(function AppFormDialog({
  open,
  app,
  onOpenChange,
}: AppFormDialogProps) {
  const [form] = Form.useForm<AppFormValues>();
  const isEdit = Boolean(app);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconUploadProgress, setIconUploadProgress] = useState(0);
  const [uploadedIconAppId, setUploadedIconAppId] = useState('');

  const title = useMemo(() => (isEdit ? '编辑应用' : '新增应用'), [isEdit]);

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      appId: app?.appId ?? '',
      displayNameKey: app?.displayNameKey ?? '',
      fallbackDisplayName: app?.fallbackDisplayName || app?.displayName || '',
      descriptionKey: app?.descriptionKey ?? '',
      fallbackDescription: app?.fallbackDescription || app?.description || '',
      iconUrl: app?.iconUrl ?? '',
      categories: app?.categories ?? [],
      status: app?.status ?? 'draft',
      reason: '',
    });
    setUploadedIconAppId('');
    setIconUploadProgress(0);
  }, [app, form, open]);

  const handleClose = () => {
    onOpenChange(false);
    form.resetFields();
    setUploadingIcon(false);
    setIconUploadProgress(0);
    setUploadedIconAppId('');
  };

  const resolveIconAppId = async () => {
    if (app?.appId) {
      return app.appId;
    }
    await form.validateFields(['appId']);
    return form.getFieldValue('appId').trim();
  };

  const handleIconFileSelect = async (file: File) => {
    try {
      const appId = await resolveIconAppId();
      setUploadingIcon(true);
      setIconUploadProgress(0);
      const asset = await appAssetUploadApi.uploadAppIcon({
        appId,
        file,
        onProgress: (progress) => setIconUploadProgress(progress.percent),
      });
      form.setFieldValue('iconUrl', asset.assetUrl);
      setUploadedIconAppId(appId);
      setIconUploadProgress(100);
      message.success('图标已上传');
    } catch (error) {
      console.error('上传应用图标失败:', error);
      message.error(
        error instanceof Error ? error.message : '上传应用图标失败',
      );
    } finally {
      setUploadingIcon(false);
    }
    return Upload.LIST_IGNORE;
  };

  const handleSubmit = async () => {
    if (uploadingIcon) {
      message.warning('图标上传中，请稍后保存');
      return;
    }
    const values = await form.validateFields();
    const appId = app?.appId ?? values.appId.trim();
    if (!app && uploadedIconAppId && uploadedIconAppId !== appId) {
      message.warning('App ID 已变化，请重新上传图标');
      return;
    }
    const base = {
      displayNameKey:
        trimOptional(values.displayNameKey) ?? defaultDisplayNameKey(appId),
      fallbackDisplayName: values.fallbackDisplayName.trim(),
      descriptionKey: trimOptional(values.descriptionKey),
      fallbackDescription: trimOptional(values.fallbackDescription),
      iconUrl: trimOptional(values.iconUrl),
      categories: normalizeCategories(values.categories),
      status: values.status,
      reason: trimOptional(values.reason),
    };

    try {
      if (app) {
        const payload: UpdateAdminAppCatalogAppRequest = {
          ...base,
          appId: app.appId,
        };
        await applicationStore.updateApp(payload);
        message.success('应用已更新');
      } else {
        const payload: CreateAdminAppCatalogAppRequest = {
          ...base,
          appId,
        };
        await applicationStore.createApp(payload);
        message.success('应用已创建');
      }
      handleClose();
    } catch (error) {
      console.error('保存应用失败:', error);
      message.error('保存应用失败');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={title}
      centered
      width={640}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button
            disabled={uploadingIcon}
            loading={applicationStore.savingApp || uploadingIcon}
            onClick={() => void handleSubmit()}
          >
            保存
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="pt-2"
        onValuesChange={(changedValues) => {
          if (
            !app &&
            'appId' in changedValues &&
            uploadedIconAppId &&
            form.getFieldValue('appId')?.trim() !== uploadedIconAppId
          ) {
            form.setFieldValue('iconUrl', '');
            setUploadedIconAppId('');
            setIconUploadProgress(0);
          }
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Form.Item
            name="appId"
            label="App ID"
            rules={[
              { required: true, message: '请输入 App ID' },
              {
                pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/,
                message: '仅支持字母、数字、点、下划线和连字符',
              },
            ]}
          >
            <Input
              disabled={isEdit}
              className="font-mono"
              placeholder="ai-media-canvas"
            />
          </Form.Item>

          <Form.Item
            name="fallbackDisplayName"
            label="展示名称"
            rules={[{ required: true, message: '请输入展示名称' }]}
          >
            <Input placeholder="AI Media Canvas" />
          </Form.Item>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Form.Item name="displayNameKey" label="名称 Key">
            <Input placeholder="app.aiMediaCanvas.name" />
          </Form.Item>

          <Form.Item name="descriptionKey" label="描述 Key">
            <Input placeholder="app.aiMediaCanvas.description" />
          </Form.Item>
        </div>

        <Form.Item name="fallbackDescription" label="兜底描述">
          <TextArea rows={3} placeholder="应用简介" />
        </Form.Item>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Form.Item name="iconUrl" label="图标 URL">
              <Input placeholder="https://..." />
            </Form.Item>
            <div className="-mt-3 flex items-center gap-3">
              <Upload
                accept="image/png,image/jpeg,image/webp"
                showUploadList={false}
                beforeUpload={(file) => handleIconFileSelect(file)}
              >
                <Button variant="outline" disabled={uploadingIcon}>
                  上传图标
                </Button>
              </Upload>
              {uploadingIcon ? (
                <Progress
                  percent={Math.round(iconUploadProgress)}
                  size="small"
                  className="min-w-28 flex-1"
                />
              ) : null}
            </div>
          </div>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select
              options={APP_STATUS_OPTIONS.map((item) => ({
                value: item.value,
                label: item.label,
              }))}
            />
          </Form.Item>
        </div>

        <Form.Item name="categories" label="分类">
          <Select
            mode="tags"
            tokenSeparators={[',']}
            placeholder="输入分类后回车"
          />
        </Form.Item>

        <Form.Item name="reason" label="操作原因">
          <TextArea rows={2} placeholder="写入审计日志，可选" />
        </Form.Item>
      </Form>
    </Modal>
  );
});
